/* nb-s-cycle.js
   v8 (2026-09-30): live trigger/range, px transforms. v7 (cycle-stack-v2). One sticky wrapper, N absolutely
   positioned cards, one continuous progress value F in [0, N-1] driving
   every card's transform. Card i's position relative to F:
     F - i <= -1        : still below, off screen (translateY 100%, opacity 0)
     -1 < F - i < 0      : entering, sliding up from below
     F - i >= 0          : at or past front; depth = min(F - i, 2) caps the
                            peek at 2 visible edges per the brief. depth 0 =
                            front (translateY 0, scale 1); depth 1/2 = 12px/
                            24px peek, slight recede in scale + brightness.
   Z-index is a literal Tailwind style already in the Liquid (later card
   always paints over an earlier one once both have appeared), so this file
   never touches stacking order, only transform/opacity/filter.

   --nb-cycle-offset: the height of whichever fixed top:0 bar is visible
   above the section right now (desktop chapter bar on the Clean Max PDP;
   0 on Home, where [data-nb-sticky-bar] does not exist on the page at
   all, and 0 on phone, where that bar pins to the bottom instead of the
   top). Re-measured on scroll (cheap: one getBoundingClientRect + one
   classList check) since the bar can become visible or hide while the
   user is already scrolling through this section. */

(function () {
  if (window.__nbCmaxCycleInit) return;
  window.__nbCmaxCycleInit = true;

  var PEEK_STEP_PX = 12;      // per depth unit, matches the 24px reserve in CSS
  var MAX_PEEK_DEPTH = 2;     // never more than 2 visible peeking edges
  var SCALE_STEP = 0.03;      // per depth unit, floor at depth 2 = 0.94
  var BRIGHTNESS_STEP = 0.05; // per depth unit, floor at depth 2 = 0.9

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Reads the Clean Max desktop chapter bar if it exists and is currently
  // pinned to the TOP of the viewport and visible. Returns 0 on Home
  // (element absent) and 0 on phone (same element, but CSS pins it to the
  // bottom there, so it never affects top clearance).
  function measureFixedTopOffset() {
    var bar = document.querySelector('[data-nb-sticky-bar]');
    if (!bar || !bar.classList.contains('is-visible')) return 0;
    var cs = window.getComputedStyle(bar);
    if (cs.position !== 'fixed') return 0;
    if (cs.top === 'auto' || parseFloat(cs.top) > 4) return 0; // bottom-pinned phone bar: top is 'auto'
    return bar.offsetHeight;
  }

  function initCycle(root) {
    if (root.__nbBound) return;
    root.__nbBound = true;

    var lines = Array.prototype.slice.call(root.querySelectorAll('[data-nb-cycle-line]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-nb-cycle-card]'));
    var linesWrap = root.querySelector('[data-nb-cycle-lines-wrap]');
    var deck = root.querySelector('[data-nb-cycle-deck]');
    var deckSticky = root.querySelector('[data-nb-cycle-deck-sticky]');

    if (!lines.length || !cards.length || !deck || !deckSticky) return;

    root.style.setProperty('--nb-cycle-line-ms', '200ms');

    var STEPS = cards.length;
    var reduced = prefersReducedMotion();

    function setActive(index) {
      lines.forEach(function (line, i) {
        var active = i === index;
        line.classList.toggle('is-active', active);
        line.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
      cards.forEach(function (card, i) {
        card.classList.toggle('is-active', i === index);
      });
    }

    root.__nbCmaxCycleSetActive = setActive;

    var trigger = 0; // scrollY at which the deck-sticky first pins
    var range = 0;   // extra scroll room while pinned

    function measure() {
      if (window.innerWidth < 900 && linesWrap) {
        root.style.setProperty('--nb-cycle-pin-top', linesWrap.offsetHeight + 'px');
      } else if (window.innerWidth >= 900) {
        root.style.setProperty('--nb-cycle-offset', measureFixedTopOffset() + 'px');
      }
      equaliseCards();
      liveTrack();
    }

    // v8: desktop cards take content height (CSS bottom: auto), so give
    // every card the tallest card's height. Otherwise a taller card behind
    // shows a stub below the front card.
    function equaliseCards() {
      cards.forEach(function (card) { card.style.minHeight = ''; });
      if (window.innerWidth < 900) return;
      var tallest = 0;
      cards.forEach(function (card) { tallest = Math.max(tallest, card.offsetHeight); });
      cards.forEach(function (card) { card.style.minHeight = tallest + 'px'; });
    }

    // v8: trigger and range are read live on every frame. v7 measured them
    // once at load, so any layout shift above the section (images, video,
    // the chapter bar changing --nb-cycle-offset) left progress running
    // late and the section released with cards still sliding in.
    function liveTrack() {
      var stickyTop = parseFloat(getComputedStyle(deckSticky).top) || 0;
      trigger = deck.getBoundingClientRect().top + window.pageYOffset - stickyTop;
      range = Math.max(0, deck.offsetHeight - deckSticky.offsetHeight);
    }
    measure();
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure);

    if (reduced) {
      // Static fallback: no sticky, no transform, plain stacked list.
      // (assets/nb-s-cycle.css also forces position: static / relative
      // under this same query as a belt-and-braces default.)
      setActive(0);
      return;
    }

    var ticking = false;
    function applyFrame() {
      ticking = false;
      // Re-measure the fixed-bar offset cheaply on every frame (desktop
      // only): the chapter bar's visibility can change mid-scroll.
      if (window.innerWidth >= 900) {
        var offset = measureFixedTopOffset();
        root.style.setProperty('--nb-cycle-offset', offset + 'px');
      }

      liveTrack();
      if (range <= 0) return;
      var travel = deckSticky.clientHeight || 1;

      var progress = Math.min(1, Math.max(0, (window.pageYOffset - trigger) / range));
      var F = progress * (STEPS - 1);

      cards.forEach(function (card, i) {
        var rel = F - i;
        var ty, scale, brightness;
        if (rel <= -1) {
          ty = travel; scale = 1; brightness = 1;
          card.style.opacity = '0';
        } else if (rel < 0) {
          var enterT = 1 + rel; // 0 -> 1 as it slides in
          ty = travel * (1 - enterT);
          scale = 1; brightness = 1;
          card.style.opacity = String(Math.min(1, enterT * 2));
        } else {
          var depth = Math.min(MAX_PEEK_DEPTH, rel);
          ty = -(depth * PEEK_STEP_PX);
          scale = 1 - depth * SCALE_STEP;
          brightness = 1 - depth * BRIGHTNESS_STEP;
          card.style.opacity = '1';
        }
        card.style.transform = 'translateY(' + ty.toFixed(1) + 'px) scale(' + scale.toFixed(4) + ')';
        card.style.filter = 'brightness(' + brightness.toFixed(4) + ')';
      });

      var active = Math.min(STEPS - 1, Math.max(0, Math.round(F)));
      setActive(active);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(applyFrame);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    function scrollToStep(index) {
      liveTrack();
      if (range <= 0) return;
      var targetY = trigger + (index / (STEPS - 1)) * range;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    lines.forEach(function (line, i) {
      line.addEventListener('click', function () {
        scrollToStep(i);
      });
    });
  }

  function scan() {
    document.querySelectorAll('[data-nb-s-cycle]').forEach(initCycle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', scan);
})();
